





































































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { DateTime } from "luxon";
import { Route } from "vue-router";
import router from "@/router/router";
import { ClientDeviceRow } from "@/data/models/ClientAdministrationRow";
import { cachedClientDevicesForAdministration, getClientDevicesForAdministration } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import ContentStateComponent from "@/components/common/ContentState.vue";
import MenuIconWithMultiSelectionListComponent from "@/components/common/MultiSelectionFilterMenu.vue";
import { setClientPageToolbarTitle } from "@/utils/ClientAdministrationTitle";
import {
  DeviceKindOption,
  DeviceListOption,
  DeviceListSort,
  deviceListOptionGroup,
  deviceListSelectionFromQuery,
  deviceListSelectionToQuery,
  deviceTypeLabelKey,
  isSensorExportDevice,
  orderedDeviceListSelection,
  PanelTypeOption,
  PeriodOption,
  selectDeviceListRows,
  SENSOR_EXPORT_OPTIONS,
} from "@/utils/ClientDeviceList";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { clientDeviceLastContactText } from "@/utils/ClientDeviceLastContact";
import { getDateForFile } from "@/utils/DateFormatter";
import { ExcelSheetSpec, downloadExcelMultiSheet } from "@/utils/DeviceExportFormats";
import { buildDeviceSensorSheet } from "@/utils/devices/SensorDataExportExtractor";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";
import { readThroughCache } from "@/utils/AdminReadCache";
import { setUpdateHold } from "@/utils/UpdateHold";

const UPDATE_HOLD_ID = "hydry-sensor-data-export";

/**
 * The Sensordaten-Export of one client in the IRES Admin Client (#2610088): the recorded sensor data
 * of the client's chosen HyDry Boxes and Panels, one worksheet per device, in an Excel file. It moved
 * here from IRES Office's settings, where every client administrator could open it.
 *
 * Former devices are offered too: a device only contributes the runs it was assigned to **this**
 * client for (`runBelongsToClient`), so nothing from another client reaches the file. Sheets are named
 * by serial number, since the portal's device rows carry no inventory number.
 *
 * Opened from the client's Geräte list with that list's filters (`filters=...` in the query, applied
 * once); back returns them. The export runs in the browser over Firestore reads every signed-in
 * session may already make, so the route guard (`MANAGE_CLIENTS_COMPONENTS`) only decides what is
 * shown, as for the Geräte list.
 */
@Component({
  components: {
    "multi-selection-menu-icon": MenuIconWithMultiSelectionListComponent,
    "content-state": ContentStateComponent,
  },
})
export default class ClientSensorDataExport extends Vue {
  devices: ClientDeviceRow[] = [];
  isLoading = true;
  /** Showing the cached devices while IRES Cloud is asked again (`AdminReadCache.ts`). */
  isRefreshing = false;
  loadError = false;
  searchQuery = "";
  /** The ticked filter options; none ticked shows every Box and Panel. */
  selection: DeviceListOption[] = [];
  /** Ticked devices by serial number; every key is set on load, so Vue tracks them. */
  selected: Record<string, boolean> = {};
  /** The moment the list was loaded, against which "vor 2 h" is told. */
  now = new Date();
  /** The Geräte list address this page was opened from: Zurück returns to that kept-alive screen. */
  devicesReturnPath: string | null = null;

  startDate = DateTime.local().minus({ months: 6 }).toISODate();
  endDate = DateTime.local().toISODate();
  startDateMenu = false;
  endDateMenu = false;

  isExporting = false;
  exportProgress = 0;
  exportTotal = 0;

  readonly filterOptions = SENSOR_EXPORT_OPTIONS;

  /**
   * Holds the "new version" banner back while the export runs: it walks the selected devices sample by
   * sample, and a reload part-way through produces no file at all. See `@/utils/UpdateHold`.
   */
  @Watch("isExporting")
  onIsExportingChanged(isExporting: boolean) {
    setUpdateHold(UPDATE_HOLD_ID, isExporting);
  }

  get clientId(): string {
    const value = this.$route.query.clientId;
    return typeof value === "string" ? value : "";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get hoverStyle(): string {
    const background = themed("", COLOR_LIST_ROW_HOVER_DARK);
    return background ? `background-color: ${background}` : "";
  }

  get rows(): ClientDeviceRow[] {
    return selectDeviceListRows(this.devices, this.selection, this.searchQuery, DeviceListSort.SERIAL_NUMBER);
  }

  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  get emptyText(): string {
    if (this.rows.length > 0) {
      return "";
    }

    return this.devices.length === 0
      ? this.$t("admin.clientSensorDataExport.none").toString()
      : this.$t("admin.clientDevices.noneMatching").toString();
  }

  get filterDescription(): string {
    return orderedDeviceListSelection(this.selection).map((option) => this.optionName(option)).join(", ");
  }

  /** Every ticked device, also those the current filter hides: the header count says how many. */
  get selectedDevices(): ClientDeviceRow[] {
    return this.devices.filter((device) => this.selected[device.serialNumber]);
  }

  get selectedCount(): number {
    return this.selectedDevices.length;
  }

  get allRowsSelected(): boolean {
    return this.rows.length > 0 && this.rows.every((device) => this.selected[device.serialNumber]);
  }

  get isDateRangeValid(): boolean {
    return this.startDate <= this.endDate;
  }

  get canExport(): boolean {
    // Not from the cached device list: the export waits for IRES Cloud's (`AdminReadCache.ts`).
    return !this.isLoading && !this.isRefreshing && !this.isExporting && this.selectedCount > 0 && this.isDateRangeValid;
  }

  optionKey(option: DeviceListOption): string {
    return option;
  }

  optionGroupName(option: DeviceListOption): string {
    return this.$t(`admin.clientDevices.filterGroups.${deviceListOptionGroup(option)}`).toString();
  }

  /** The Geräte list's names for its options. */
  optionName(option: DeviceListOption): string {
    switch (option) {
      case PanelTypeOption.HYDRY_PANEL:
        return this.$t("iresDeviceType.panel").toString();
      case PanelTypeOption.HYDRY_SLIM:
        return this.$t("iresDeviceType.slim").toString();
      case PanelTypeOption.HYDRY_EDGE:
        return this.$t("iresDeviceType.edge").toString();
      case DeviceKindOption.PANELS:
      case DeviceKindOption.BOXES:
      case DeviceKindOption.MINIS:
        return this.$t(`admin.clientDevices.kinds.${option}`).toString();
      case PeriodOption.CURRENT:
      case PeriodOption.FORMER:
        return this.$t(`admin.clientDevices.scopes.${option}`).toString();
      default:
        return this.assignmentTypeLabel(option);
    }
  }

  assignmentTypeLabel(type: string): string {
    const key = `admin.clients.detail.devices.assignmentTypes.${type}`;
    return this.$te(key) ? this.$t(key).toString() : type;
  }

  deviceTypeLabel(device: ClientDeviceRow): string {
    return this.$t(deviceTypeLabelKey(device)).toString();
  }

  /**
   * When the device last reported ("IoT vor 2 h"), which tells more about recent use than its
   * assignment type (Taimoor, 2026-10-27); the type stays available as a filter.
   */
  lastContactLabel(device: ClientDeviceRow): string {
    return clientDeviceLastContactText(this, this.now, device);
  }

  isSelected(serialNumber: string): boolean {
    return this.selected[serialNumber] === true;
  }

  toggle(serialNumber: string) {
    this.$set(this.selected, serialNumber, !this.isSelected(serialNumber));
  }

  /** Ticks every shown row, or clears them once all are ticked; hidden rows keep their state. */
  toggleAllRows() {
    const value = !this.allRowsSelected;
    this.rows.forEach((device) => this.$set(this.selected, device.serialNumber, value));
  }

  formatDisplayDate(isoDate: string): string {
    return DateTime.fromISO(isoDate).toFormat("dd.MM.yyyy");
  }

  /**
   * Remembers the Geräte list it came from. Its filters may have changed since it opened, so
   * rebuilding the address from them would miss the cached list and load it again.
   */
  beforeRouteEnter(to: Route, from: Route, next: (callback: (vm: Vue) => void) => void) {
    next((vm) => {
      if (from.name === ComponentName.CLIENT_ADMINISTRATION_DEVICES && from.query.clientId === to.query.clientId) {
        (vm as ClientSensorDataExport).devicesReturnPath = from.fullPath;
      }
    });
  }

  onBack() {
    if (this.devicesReturnPath) {
      router.push(this.devicesReturnPath);
      return;
    }

    router.push({
      name: ComponentName.CLIENT_ADMINISTRATION_DEVICES,
      query: { clientId: this.clientId, ...deviceListSelectionToQuery(this.selection) },
    });
  }

  /**
   * The cached devices at once when this client's were read before in this tab, then IRES Cloud's. A
   * tick made meanwhile is kept for a device still in the fresh list.
   */
  async load() {
    const cached = this.clientId === "" ? null : cachedClientDevicesForAdministration(this.clientId);
    this.isLoading = cached === null;
    this.isRefreshing = cached !== null;
    this.loadError = false;

    try {
      await readThroughCache(
        cached,
        () => (this.clientId === "" ? Promise.resolve([]) : getClientDevicesForAdministration(this.clientId)),
        (devices) => {
          this.devices = devices.filter(isSensorExportDevice);
          this.now = new Date();
          const selected: Record<string, boolean> = {};
          this.devices.forEach((device) => (selected[device.serialNumber] = this.selected[device.serialNumber] === true));
          this.selected = selected;
        }
      );
    } catch {
      // Swallowed on purpose: the screen shows its own failure state (a refusal from Cloud included).
      this.loadError = true;
      this.devices = [];
    } finally {
      this.isLoading = false;
      this.isRefreshing = false;
    }
  }

  async onExport() {
    if (!this.canExport) {
      return;
    }

    const devices = this.selectedDevices;
    this.isExporting = true;
    this.exportTotal = devices.length;
    this.exportProgress = 0;

    const startMillis = DateTime.fromISO(this.startDate).startOf("day").toMillis();
    const endMillis = DateTime.fromISO(this.endDate).endOf("day").toMillis();
    const usedSheetNames = new Set<string>();
    const sheets: ExcelSheetSpec[] = [];

    try {
      for (const device of devices) {
        // An empty label names the sheet by its serial number alone.
        const sheet = await buildDeviceSensorSheet(this.clientId, device.serialNumber, "", usedSheetNames, startMillis, endMillis);

        if (sheet) {
          sheets.push(sheet);
        }

        this.exportProgress++;
      }

      if (sheets.length === 0) {
        SnackbarModule.showError({ key: "admin.clientSensorDataExport.noData" });
        return;
      }

      await downloadExcelMultiSheet(sheets, `HYDRY-SENSORDATEN-${getDateForFile(new Date())}`);
      SnackbarModule.showSuccess({ key: "admin.clientSensorDataExport.exportSuccess" });
    } catch {
      SnackbarModule.showError({ key: "admin.clientSensorDataExport.exportError" });
    } finally {
      this.isExporting = false;
    }
  }

  mounted() {
    setClientPageToolbarTitle(
      this.clientId,
      this.$t("admin.clientDevices.title").toString(),
      this.$t("admin.clientSensorDataExport.title").toString()
    );
    // The Geräte list's filters, applied once; a Minis option has nothing to show here.
    this.selection = deviceListSelectionFromQuery(this.$route.query as Record<string, unknown>).filter((option) =>
      SENSOR_EXPORT_OPTIONS.includes(option)
    );
    this.load();
  }

  beforeDestroy() {
    setUpdateHold(UPDATE_HOLD_ID, false);
  }
}
